import type { UiLocale } from "@lib/locale";
import { DEFAULT_NATIVE_LOCALE, DEFAULT_TARGET_LOCALE } from "@lib/courseConstants";

/** Target + native copy for curriculum entities (keys are semantic, not BCP-47). */
export type LocalizedTitles = { target: string; native: string };

export type TitleLocaleContext = {
  targetLocale: string;
  nativeLocale: string;
  /** Locales the stored title strings were authored for. */
  contentTargetLocale?: string;
  contentNativeLocale?: string;
};

export function parseLocalizedTitles(json: unknown): LocalizedTitles {
  if (json && typeof json === "object") {
    const row = json as Record<string, unknown>;
    if ("target" in row || "native" in row) {
      return {
        target: typeof row.target === "string" ? row.target : "",
        native: typeof row.native === "string" ? row.native : "",
      };
    }
    // Legacy seed shape before course-aware titles
    if ("pt" in row) {
      return {
        target: typeof row.pt === "string" ? row.pt : "",
        native: typeof row.en === "string" ? row.en : "",
      };
    }
  }
  return { target: "", native: "" };
}

/** Map stored title fields onto the learner's active target/native locales. */
export function resolveTitleTexts(
  titles: LocalizedTitles,
  {
    targetLocale,
    nativeLocale,
    contentTargetLocale = DEFAULT_TARGET_LOCALE,
    contentNativeLocale = DEFAULT_NATIVE_LOCALE,
  }: TitleLocaleContext,
): { targetText: string; nativeText: string } {
  const byLocale: Record<string, string> = {};
  if (titles.target) byLocale[contentTargetLocale] = titles.target;
  if (titles.native) byLocale[contentNativeLocale] = titles.native;

  return {
    targetText: byLocale[targetLocale] ?? "",
    nativeText: byLocale[nativeLocale] ?? "",
  };
}

export function targetLanguageText(
  titles: LocalizedTitles,
  context?: Pick<TitleLocaleContext, "targetLocale" | "nativeLocale">,
): string {
  if (context) {
    const { targetText, nativeText } = resolveTitleTexts(titles, context);
    return targetText || nativeText;
  }
  return titles.target || titles.native;
}

export function resolveDisplayTitles(
  titles: LocalizedTitles,
  uiLocale: UiLocale,
  context: TitleLocaleContext,
): { primary: string; secondary: string } {
  const { targetText, nativeText } = resolveTitleTexts(titles, context);

  function pair(primary: string, secondary: string) {
    return {
      primary: primary || secondary,
      secondary: primary && secondary && primary !== secondary ? secondary : "",
    };
  }

  if (uiLocale === context.nativeLocale) {
    return pair(nativeText, targetText);
  }

  if (uiLocale === context.targetLocale) {
    return pair(targetText, nativeText);
  }

  return pair(targetText, nativeText);
}
